<template>
  <section>
    <q-btn
      v-bind="cta"
      padding="sm md"
    />
  </section>
</template>

<script lang="ts" setup>
import { mdiCartCheck, mdiCartPlus } from '@quasar/extras/mdi-v7';
import type { QBtnProps } from 'quasar';
import type { Product } from '~/types/search';

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
});

const cartStore = useCartStore();
const { notify, add, quote, select, path, availableInStore } = useCatalog(props.product);

const cartItem = computed(() => cartStore.data.items.find(i => i.productId == props.product.id));

const isInStock = computed(() => !!props.product.isInStock);
const isVariable = computed(() => !!props.product.isVariable);
const isInStore = computed(() => !!props.product.isInStore);
const isNotify = computed(() => !isInStock.value && props.product.orderType === 'Notify');
const isMapEmail = computed(() => props.product.mapPolicy === 'Email');
const isSigStyle = computed(() => props.product.style?.startsWith?.('SIG-') === true);

const canPickupToday = computed(() =>
  availableInStore.value && (isInStore.value || !isInStock.value));

// Build a single CTA object
const cta = computed<QBtnProps>(() => {
  // 1) Pick up today takes top priority if available
  if (canPickupToday.value) {
    return {
      'label': 'Pick Up Today',
      'color': 'primary',
      'outline': true,
      'to': path.value,
      'aria-label': 'Pick up in store today',
    };
  }

  // 2) Variable products -> show options (typically *don’t* navigate + click handler)
  if (isVariable.value) {
    return {
      'label': 'See Options',
      'color': 'primary',
      'flat': true,
      'aria-label': 'See available options',
      'onClick': () => select(),
    };
  }

  // 3) Not in stock + notify
  if (isNotify.value) {
    return {
      'label': 'Get Notified',
      'color': 'primary',
      'padding': 'sm md',
      'outline': true,
      'aria-label': 'Get notified when back in stock',
      'onClick': () => notify(),
    };
  }

  // 4) Not in stock (no notify)
  if (!isInStock.value) {
    return {
      'label': 'Out Of Stock',
      'color': 'negative',
      'flat': true,
      'disable': true,
      'aria-label': 'Out of stock',
    };
  }

  // 5) MAP email pricing flow
  if (isMapEmail.value) {
    return {
      'label': isSigStyle.value ? 'Email For Price' : 'Get Quote',
      'color': 'primary',
      'padding': 'sm md',
      'outline': true,
      'aria-label': isSigStyle.value ? 'Email for price' : 'Request a quote',
      'onClick': () => quote(),
    };
  }

  // 6) Default: Add to cart
  const inCartQty = cartItem.value?.quantity ?? 0;
  const inCart = inCartQty > 0;

  return {
    'icon': inCart ? mdiCartCheck : mdiCartPlus,
    'loading': add.status.value === 'pending',
    'disable': !!cartStore.pending,
    'color': inCart ? 'positive' : 'primary',
    'label': inCart ? `In Cart (${inCartQty})` : 'Add To Cart',
    'padding': 'sm md',
    'aria-label': inCart ? `In cart, quantity ${inCartQty}` : 'Add to cart',
    'onClick': () => add.execute(),
  };
});
</script>
